/**
 * AdminPanel.tsx
 * Panel d'administration complet pour Snapbooth
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminStats, type AdminStats } from '../../services/AdminService';
import { supabase as supabaseClient } from '../../services/SupabaseService';
import { logger } from '../../utils/ProfessionalLogger';
import { useToast } from '../react/common/ToastProvider';

// A24 : encart d'erreur inline réutilisable pour les onglets admin (au lieu de
// listes vides indistinguables d'un échec réseau, ou d'erreurs avalées).
const AdminErrorBox: React.FC<{ message: string; onRetry: () => void }> = ({
  message,
  onRetry,
}) => (
  <div
    role="alert"
    style={{
      textAlign: 'center',
      padding: '40px 24px',
      background: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      color: '#991b1b',
    }}
  >
    <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
    <div style={{ fontWeight: 600, marginBottom: '4px' }}>Erreur de chargement</div>
    <div style={{ fontSize: '14px', color: '#b91c1c', marginBottom: '16px' }}>{message}</div>
    <button
      onClick={onRetry}
      style={{
        padding: '8px 16px',
        background: '#dc2626',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
      }}
    >
      Réessayer
    </button>
  </div>
);

// Façade attendue par le panel : `supabaseService.supabase.<...>`.
// `supabaseClient` est le client Supabase brut (ou null si non configuré).
const supabaseService = { supabase: supabaseClient } as {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabase: any;
};

export const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'templates' | 'users'>('dashboard');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setStatsError(null);
      // PR-10 : Utiliser AdminService.getAdminStats() - une seule requête RPC
      const result = await getAdminStats();

      if (result.error) {
        logger.error('Erreur lors du chargement des stats:', result.error);
        setStats(null);
        setStatsError('Impossible de charger les statistiques.');
      } else if (result.data) {
        // Adapter les données de l'API : on garde les deux formes (snake + camel).
        setStats({
          ...result.data,
          totalUsers: result.data.users,
          totalProjects: result.data.projects,
          publicTemplates: result.data.templates_publics,
          totalExports: result.data.exports,
          totalStorageBytes: result.data.storage_estimate,
        });
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des stats:', error);
      setStats(null);
      setStatsError('Impossible de charger les statistiques.');
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <header
        style={{
          background: 'white',
          borderBottom: '1px solid #e2e8f0',
          padding: '16px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 600, margin: 0, color: '#1e293b' }}>
            Panel d'Administration
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>
            Gestion de l'application Snapbooth
          </p>
        </div>
        <button
          onClick={() => navigate('/editor')}
          style={{
            padding: '8px 16px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          Retour à l'éditeur
        </button>
      </header>

      {/* Tabs */}
      <div
        style={{
          background: 'white',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          gap: '8px',
          padding: '0 24px',
        }}
      >
        {(['dashboard', 'templates', 'users'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 20px',
              background: activeTab === tab ? '#eff6ff' : 'transparent',
              color: activeTab === tab ? '#3b82f6' : '#64748b',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid #3b82f6' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab ? 600 : 400,
              textTransform: 'capitalize',
            }}
          >
            {tab === 'dashboard'
              ? 'Tableau de bord'
              : tab === 'templates'
                ? 'Templates'
                : 'Utilisateurs'}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: '24px', overflow: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>
            Chargement...
          </div>
        ) : activeTab === 'dashboard' ? (
          <DashboardTab
            stats={stats}
            formatBytes={formatBytes}
            error={statsError}
            onRetry={loadStats}
          />
        ) : activeTab === 'templates' ? (
          <TemplatesTab />
        ) : (
          <UsersTab />
        )}
      </div>
    </div>
  );
};

/**
 * Onglet Dashboard
 */
const DashboardTab: React.FC<{
  stats: AdminStats | null;
  formatBytes: (bytes: number) => string;
  error?: string | null;
  onRetry: () => void;
}> = ({ stats, formatBytes, error, onRetry }) => {
  if (error) {
    return <AdminErrorBox message={error} onRetry={onRetry} />;
  }
  if (!stats) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>Aucune donnée</div>
    );
  }

  const statCards = [
    { label: 'Utilisateurs', value: stats.totalUsers, icon: '👥', color: '#3b82f6' },
    { label: 'Projets', value: stats.totalProjects, icon: '📁', color: '#10b981' },
    { label: 'Templates publics', value: stats.publicTemplates, icon: '🎨', color: '#f59e0b' },
    { label: 'Exports', value: stats.totalExports, icon: '📤', color: '#8b5cf6' },
    {
      label: 'Stockage',
      value: formatBytes(stats.totalStorageBytes ?? 0),
      icon: '💾',
      color: '#ef4444',
    },
  ];

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px', color: '#1e293b' }}>
        Statistiques générales
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px',
        }}
      >
        {statCards.map((card, index) => (
          <div
            key={index}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div style={{ fontSize: '32px' }}>{card.icon}</div>
            <div style={{ fontSize: '32px', fontWeight: 700, color: card.color }}>
              {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>{card.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Onglet Templates
 */
const TemplatesTab: React.FC = () => {
  interface TemplateItem {
    id: string;
    name: string;
    [key: string]: unknown;
  }
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  // A13 : toggle publish/unpublish d'un template.
  const handleTogglePublic = async (templateId: string, currentStatus: boolean) => {
    try {
      const { error: toggleError } = await supabaseService.supabase
        .from('template_editor_templates')
        .update({ is_public: !currentStatus })
        .eq('id', templateId);
      if (toggleError) throw toggleError;
      addToast({
        type: 'success',
        message: currentStatus ? 'Template dépublié.' : 'Template publié.',
      });
      await loadTemplates();
    } catch (e) {
      logger.error('Erreur toggle public:', e);
      addToast({ type: 'error', message: 'Erreur lors du changement de visibilité.' });
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabaseService.supabase
        .from('template_editor_templates')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      logger.error('Erreur lors du chargement des templates:', error);
      // A24 : exposer l'échec au lieu de laisser une liste vide trompeuse.
      setError('Impossible de charger les templates.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>Chargement...</div>
    );
  }

  if (error) {
    return <AdminErrorBox message={error} onRetry={loadTemplates} />;
  }

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px', color: '#1e293b' }}>
        Gestion des Templates
      </h2>
      <div
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {templates.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
            Aucun template
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Nom
                </th>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Public
                </th>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Créé le
                </th>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {templates.map(template => (
                <tr key={template.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {template.name || 'Sans nom'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {/* A13 : toggle publish/unpublish (plus de valeur read-only) */}
                    <button
                      onClick={() => handleTogglePublic(template.id, !!template.is_public)}
                      style={{
                        padding: '4px 10px',
                        background: template.is_public ? '#dcfce7' : '#f1f5f9',
                        color: template.is_public ? '#166534' : '#475569',
                        border: `1px solid ${template.is_public ? '#86efac' : '#cbd5e1'}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                      title={template.is_public ? 'Dépublier ce template' : 'Publier ce template'}
                    >
                      {template.is_public ? '✅ Publié' : '⬜ Privé'}
                    </button>
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {new Date(template.created_at as string).toLocaleDateString('fr-FR')}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    <button
                      style={{
                        padding: '4px 8px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                      onClick={async () => {
                        if (!confirm('Supprimer ce template ?')) return;
                        // A24 : remonter le résultat (succès/échec) à l'utilisateur.
                        const { error: delError } = await supabaseService.supabase
                          .from('template_editor_templates')
                          .delete()
                          .eq('id', template.id);
                        if (delError) {
                          logger.error('Erreur suppression template:', delError);
                          addToast({
                            type: 'error',
                            message: 'Échec de la suppression du template.',
                          });
                          return;
                        }
                        addToast({ type: 'success', message: 'Template supprimé.' });
                        loadTemplates();
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/**
 * Onglet Utilisateurs
 */
const UsersTab: React.FC = () => {
  interface UserItem {
    id: string;
    email: string;
    [key: string]: unknown;
  }
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { addToast } = useToast();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabaseService.supabase
        .from('profiles')
        .select('id, email, full_name, is_admin, created_at')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      logger.error('Erreur lors du chargement des utilisateurs:', error);
      // A24 : exposer l'échec au lieu de laisser une liste vide trompeuse.
      setError('Impossible de charger les utilisateurs.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabaseService.supabase
        .from('profiles')
        .update({ is_admin: !currentStatus })
        .eq('id', userId);

      if (error) throw error;
      addToast({
        type: 'success',
        message: currentStatus ? 'Droits admin retirés.' : 'Droits admin accordés.',
      });
      await loadUsers();
    } catch (error) {
      logger.error('Erreur lors de la modification du rôle:', error);
      // A24 : feedback via toast au lieu d'un alert() brut.
      addToast({ type: 'error', message: 'Erreur lors de la modification du rôle.' });
    }
  };

  const handleDeleteUser = (_userId: string) => {
    // A06/A14 : la suppression d'un compte via `auth.admin.deleteUser` exige la
    // clé service_role, qui ne doit JAMAIS être exposée côté navigateur (seule la
    // clé anon l'est). L'appel échouait donc systématiquement (403). On le
    // désactive et on explique clairement la marche à suivre au lieu de simuler
    // une action destructive qui ne fonctionne pas.
    logger.warn(
      'Suppression de compte demandée mais non disponible côté client (nécessite une Edge Function avec service_role).'
    );
    // A24 : feedback via toast au lieu d'un alert() brut.
    addToast({
      type: 'info',
      message:
        "La suppression d'un compte nécessite une fonction serveur sécurisée (Edge Function service_role). Indisponible côté navigateur.",
      duration: 7000,
    });
  };

  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase();
    const email = (user.email as string | null) ?? '';
    const fullName = (user.full_name as string | null) ?? '';
    const id = String(user.id ?? '');
    return (
      email.toLowerCase().includes(search) ||
      fullName.toLowerCase().includes(search) ||
      id.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#64748b' }}>Chargement...</div>
    );
  }

  if (error) {
    return <AdminErrorBox message={error} onRetry={loadUsers} />;
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}
      >
        <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#1e293b' }}>
          Gestion des Utilisateurs
        </h2>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Rechercher un utilisateur..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              width: '300px',
            }}
          />
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div
        style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {filteredUsers.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
            {searchTerm ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Email
                </th>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Nom
                </th>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Admin
                </th>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Inscrit le
                </th>
                <th
                  style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: 600 }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={String(user.id)} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {(user.email as string) || 'N/A'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {(user.full_name as string) || 'Non renseigné'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    <button
                      onClick={() => handleToggleAdmin(String(user.id), Boolean(user.is_admin))}
                      style={{
                        padding: '4px 12px',
                        background: user.is_admin ? '#10b981' : '#e5e7eb',
                        color: user.is_admin ? 'white' : '#374151',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}
                    >
                      {user.is_admin ? '✅ Admin' : '👤 User'}
                    </button>
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    {user.created_at
                      ? new Date(user.created_at as string).toLocaleDateString('fr-FR')
                      : 'N/A'}
                  </td>
                  <td style={{ padding: '12px', fontSize: '14px' }}>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      title="Indisponible côté client — nécessite une fonction serveur (service_role)"
                      style={{
                        padding: '4px 8px',
                        background: '#e2e8f0',
                        color: '#64748b',
                        border: '1px solid #cbd5e1',
                        borderRadius: '4px',
                        cursor: 'not-allowed',
                        fontSize: '12px',
                      }}
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
